
import React, { useState } from 'react';
import { Upload, FileImage, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';

interface UploadSectionProps {
  onImageUploaded: (file: File, imageUrl: string) => void;
  isLoading: boolean;
}

const UploadSection: React.FC<UploadSectionProps> = ({ onImageUploaded, isLoading }) => {
  const [dragActive, setDragActive] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const { toast } = useToast();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const validateFile = (file: File): boolean => {
    // Check if file is a JPG
    if (!file.type.match('image/jpeg')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a JPG file only.',
        variant: 'destructive',
      });
      return false;
    }

    // Check file size (limit to 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 2MB.',
        variant: 'destructive',
      });
      return false;
    }

    return true;
  };

  // Compress and resize image to max 600x900 dimensions
  const compressImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions (max 600x900, maintain aspect ratio)
          let width = img.width;
          let height = img.height;
          const maxWidth = 600;
          const maxHeight = 900;

          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (aspectRatio > maxWidth / maxHeight) {
              // Width is the limiting factor
              width = maxWidth;
              height = Math.round(width / aspectRatio);
            } else {
              // Height is the limiting factor
              height = maxHeight;
              width = Math.round(height * aspectRatio);
            }
          }

          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression (quality 0.85)
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              console.log('Image compressed:', {
                original: `${Math.round(file.size / 1024)}KB`,
                compressed: `${Math.round(compressedFile.size / 1024)}KB`,
                dimensions: `${width}x${height}`,
                reduction: `${Math.round((1 - compressedFile.size / file.size) * 100)}%`
              });
              resolve(compressedFile);
            },
            'image/jpeg',
            0.85
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        try {
          const compressedFile = await compressImage(droppedFile);
          handleFile(compressedFile);
        } catch (error) {
          console.error('Error compressing image:', error);
          toast({
            title: 'Compression failed',
            description: 'Failed to process image. Please try again.',
            variant: 'destructive',
          });
        }
      }
    }
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        try {
          const compressedFile = await compressImage(selectedFile);
          handleFile(compressedFile);
        } catch (error) {
          console.error('Error compressing image:', error);
          toast({
            title: 'Compression failed',
            description: 'Failed to process image. Please try again.',
            variant: 'destructive',
          });
        }
      }
    }
  };

  const handleFile = (file: File) => {
    setFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreviewImage(objectUrl);
    onImageUploaded(file, objectUrl);
  };

  const clearImage = () => {
    if (previewImage) {
      URL.revokeObjectURL(previewImage);
    }
    setPreviewImage(null);
    setFile(null);
  };

  return (
    <div className="w-full max-w-xl mx-auto my-8 px-4">
      {!previewImage ? (
        <div
          className={`border-2 border-dashed rounded-lg p-12 text-center ${
            dragActive ? 'border-bookNavy bg-blue-50' : 'border-gray-300'
          }`}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
        >
          <FileImage className="w-16 h-16 mx-auto mb-4 text-bookGray" />
          <h3 className="text-xl font-serif font-medium mb-2">Upload Your Book Cover</h3>
          <p className="text-bookGray mb-6">Drag and drop your JPG file here, or click to browse</p>
          <p className="text-sm text-bookGray mb-6">Maximum file size: 2MB</p>
          <Button
            className="cursor-pointer bg-bookNavy hover:bg-bookNavy/90"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <Upload className="mr-2 h-4 w-4" />
            Browse Files
          </Button>
          <input
            id="file-upload"
            type="file"
            accept=".jpg,.jpeg"
            onChange={handleChange}
            className="hidden"
            disabled={isLoading}
          />
        </div>
      ) : (
        <div className="relative book-cover-wrapper">
          <Button
            variant="secondary"
            size="icon"
            className="absolute top-2 right-2 z-10 bg-white/80 hover:bg-white"
            onClick={clearImage}
            disabled={isLoading}
          >
            <X className="h-4 w-4" />
          </Button>
          <img 
            src={previewImage} 
            alt="Book cover preview" 
            className="w-full h-auto rounded-md shadow-md" 
          />
        </div>
      )}
    </div>
  );
};

export default UploadSection;
